import DateJS, { DateType, format2DateStr } from '@/utils/date';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import getRandomId from '@/utils/uuid';
import BaseService from '@/api/checkInBase';
import { getAllChannelWithCache, getReserveChannelWithCache } from '@/api/cacheRequest';
import {
  ConfigRoomPrice,
  RentTimeInfo,
  ChannelItem,
  RoomCategory,
  RoomItem,
  CouponValue,
  CheckInGuestInfo,
  RentRoomInfo,
} from '@/redux/reducers/reservation/base.d';

import {
  CertIDType,
  MemberType,
  OccType,
  RoomCleanStatus,
  RoomMainStatus,
  ERoomArrangeType,
  CustomUnableBookStatus,
  EnableBookEnum,
} from '@/config/constant';
import { fetchCouponCalculate } from '@/api/guest';

// redux action覆写getState方法
export const createActionMiddleware: <T>(
  storeName: string,
) => (next: (dispatch: (action) => void, getState: () => T) => any) => any = (storeName) => (
  next,
) => (dispatch, getState) => {
  return next(dispatch, () => getState()[storeName]);
};

// 创建 mapStateToProps函数
export const createStateSelector: <T>(
  storeName: string,
  selector: (store: T, globalStore?) => any,
) => (store: T) => any = (storeName, selector) => {
  return (store) => {
    return selector(store[storeName], store);
  };
};

// 获取入住时间信息
export const getRentTimeInfo: (
  timeInfo: { dayTypeTimeInfo: RentTimeInfo; hourTypeTimeInfo: RentTimeInfo },
  occType,
) => RentTimeInfo = (timeInfo, occType) => {
  return {
    [OccType.DayUse]: timeInfo.dayTypeTimeInfo,
    [OccType.TimeRENT]: timeInfo.hourTypeTimeInfo,
  }[occType];
};

// 获取日租房预离时间
export const getDayDepartureTime: (timeInfo: RentTimeInfo, departureTime: string) => DateType = (
  timeInfo,
  departureTime,
) => {
  const { businessDate, rentTime } = timeInfo;
  return DateJS(businessDate)
    .startOf('day')
    .add(rentTime || 0, 'day')
    .add(DateJS.duration(departureTime));
};

// 获取渠道列表(当前门店启用的渠道)
export const getChannelList: () => Promise<ChannelItem[]> = async () => {
  let result: ChannelItem[] = [];
  try {
    const res = await getReserveChannelWithCache();
    result = res.data || [];
  } catch (_) {
    result = [];
  }
  return result;
};

// 获取渠道列表(所有渠道)
export const getAllChannelList: () => Promise<ChannelItem[]> = async () => {
  let result: ChannelItem[] = [];
  try {
    const res = await getAllChannelWithCache();
    result = res.data || [];
  } catch (_) {
    result = [];
  }
  return result;
};

// 获取营业日
export const getBusinessDate: (date: DateType) => Promise<DateType> = async (date) => {
  let result = DateJS().startOf('day'); // 默认今天
  try {
    if (date) {
      const res = await BaseService.getBusinessDate({
        dateTime: DateJS(date).valueOf(),
      });
      result = DateJS(res.data.localDate);
    }
  } catch (_) {
    // return undefined;
  }
  return result;
};

// 获取上一次夜审时间
export const getLastAuditTime: () => Promise<DateType> = async () => {
  try {
    const res = await BaseService.getLastAuditDate();
    return DateJS(res.data.startTime);
  } catch (_) {
    return DateJS().startOf('day'); // 默认
  }
};

// 获取入住时间
interface RentDateItem {
  rentDateId: string;
  rentDate: string;
}
export const getRentDayArr: (data: {
  rentTimeInfo: RentTimeInfo;
  occType: OccType;
  rentRoomInfos?: RentRoomInfo[];
}) => RentDateItem[] = ({ rentTimeInfo, occType, rentRoomInfos }) => {
  const rentDateArr: RentDateItem[] = [];
  // 复用之前房间的日期
  const originPriceList = rentRoomInfos && rentRoomInfos.length && rentRoomInfos[0].roomPriceList;
  if (originPriceList && originPriceList.length) {
    originPriceList.forEach((item) => {
      rentDateArr.push({
        rentDateId: item.rentDateId,
        rentDate: item.rentDate,
      });
    });
  } else if (rentTimeInfo && rentTimeInfo.expectedArrivalTime && rentTimeInfo.rentTime) {
    // 没有入住房间 重新计算入住日期
    const { businessDate, rentTime } = rentTimeInfo;
    if (occType === OccType.TimeRENT) {
      // 钟点房 入住日期为营业日
      rentDateArr.push({
        rentDateId: getRandomId(),
        rentDate: format2DateStr(businessDate),
      });
    }
    if (occType === OccType.DayUse) {
      // 日租房 入住时间为营业日+入住天数
      const arrivalDate = DateJS(businessDate);
      for (let idx = 0; idx < rentTime; ) {
        rentDateArr.push({
          rentDateId: getRandomId(),
          rentDate: format2DateStr(arrivalDate.clone().add(idx, 'day')),
        });
        idx += 1;
      }
    }
  }
  return rentDateArr;
};

// 数组按字段转为对象索引
export const flatArrByKey: <T>(list: T[], indexKey: keyof T) => { [key: string]: T } = (
  list,
  indexKey,
) => {
  const result = {};
  (list || []).forEach((item) => {
    result[(item[indexKey] as unknown) as string] = item;
  });
  return result;
};

type FilterArrByKey = {
  <T>(list: T[], indexKey: keyof T, target: unknown): T[];
  <T>(list: T[], indexKey: (item: T) => boolean): T[];
};
// 根据指定条件筛选数组
export const filterArrByKey: FilterArrByKey = (list, indexKey, target?) => {
  const filterFunc =
    typeof indexKey === 'function' ? indexKey : (item) => item[indexKey] === target;
  return (list || []).filter(filterFunc);
};

type FindItemByKey = {
  <T>(list: T[], indexKey: keyof T, target: unknown): T;
  <T>(list: T[], indexKey: (item: T) => boolean): T;
};
// 按指定条件查找数据
export const findItemByKey: FindItemByKey = (list, indexKey, target?) => {
  const filterFunc =
    typeof indexKey === 'function' ? indexKey : (item) => item[indexKey] === target;
  return (list || []).find(filterFunc);
};

interface FlatRoomData {
  [roomTypeId: string]: {
    roomTypeName: string;
    roomListMap: {
      [roomId: string]: RoomItem;
    };
  };
}

// 房间数据转为对象
export const flatRoomList: (roomList: RoomCategory[]) => FlatRoomData = (roomList) => {
  const result: FlatRoomData = {};
  (roomList || []).forEach((category) => {
    result[category.id] = {
      roomTypeName: category.name,
      roomListMap: flatArrByKey(category.roomList, 'id'),
    };
  });
  return result;
};

interface FullRoomInfo extends RoomItem {
  roomTypeName: string;
}

// 获取房间数据
export const getRoomItemData: (
  data: FlatRoomData,
  roomTypeId: number,
  roomId: number,
) => FullRoomInfo = (data, roomTypeId, roomId) => {
  const { roomTypeName, roomListMap } = data[roomTypeId] || {};
  const roomItem = roomListMap?.[roomId];
  return {
    ...roomItem,
    roomTypeId,
    roomTypeName,
  };
};

// 房型价格转map
export const flatPriceList: (
  list: ConfigRoomPrice[],
) => {
  cachePriceMap: { [typeId: string]: { [date: string]: string } };
  cacheDepositMap: { [typeId: string]: string };
} = (list) => {
  const cachePriceMap = {}; // 房价 roomTypeId->日期->价格
  const cacheDepositMap = {}; // 押金 roomTypeId->押金
  list.forEach((item) => {
    const { roomTypeId, priceList, deposit } = item;
    const typePriceMap = {};
    (priceList || []).forEach((dayInfo) => {
      typePriceMap[dayInfo.date] = parseFen2Yuan(dayInfo.realPrice);
    });
    cacheDepositMap[roomTypeId] = parseFen2Yuan(deposit);
    cachePriceMap[roomTypeId] = typePriceMap;
  });
  return {
    cachePriceMap,
    cacheDepositMap,
  };
};

// 生成空的优惠券信息
export const getEmptyCouponData: () => CouponValue = () => {
  return {
    couponAmount: null,
    couponName: null,
    couponNo: null,
    couponType: null,
    couponWyuid: null,
    couponValue: null,
    couponSubType: null,
  };
};

// 生成默认的入住时间
export const createDefaultRentTime: (
  arrivalTime?: number, // 默认入住时间
) => {
  dayTypeTimeInfo: RentTimeInfo;
  hourTypeTimeInfo: RentTimeInfo;
} = (arrivalTime) => {
  const nowDate = DateJS(arrivalTime);
  const defaultDayVal = 1;
  const defaultHourVal = 3;
  return {
    dayTypeTimeInfo: {
      occType: OccType.DayUse,
      businessDate: nowDate.clone().startOf('day'),
      expectedArrivalTime: nowDate.clone(),
      rentTime: defaultDayVal,
      expectedDepartureTime: getDayDepartureTime(
        { businessDate: nowDate.clone(), rentTime: defaultDayVal } as RentTimeInfo,
        '12:00:00',
      ),
    },
    hourTypeTimeInfo: {
      occType: OccType.TimeRENT,
      businessDate: nowDate.clone().startOf('day'),
      expectedArrivalTime: nowDate.clone(),
      rentTime: defaultHourVal, // 默认3小时
      expectedDepartureTime: nowDate.clone().add(defaultHourVal, 'hour'),
    },
  };
};

// 生成入住宾客基础信息
export const createGuestItem: (defaultData: Partial<CheckInGuestInfo>) => CheckInGuestInfo = (
  defaultData,
) => {
  // 默认是宾客初始数据
  const initData: CheckInGuestInfo = {
    indexId: getRandomId(),
    name: null,
    certType: CertIDType.ID_CARD,
    certNo: null,
    mobile: null,
    email: null,
    isMember: false,
    memberLevel: undefined,
    remark: null,
    memberType: MemberType.CheckIn,
    wyuId: null,
    balance: null,
    accountType: null,
    source: 'INPUT', // 默认是输入的
    nation: null,
    sex: null,
    birthday: null,
    certificateLocation: null,
    issuingAuthority: null,
    certificateStartTime: null,
    certificateEndTime: null,
  };
  return {
    ...initData,
    ...defaultData,
  };
};

// 入住换排房-房间是否可以入住
export const getRoomCheckInStatus: (room: RoomItem) => boolean = (room) => {
  // 在住房 和 脏房 以及“停” 满” “原”  不允许办理入住
  return (
    room.mainStatus !== RoomMainStatus.Occupied &&
    room.cleanStatus !== RoomCleanStatus.Dirty &&
    room?.bookingInfo?.enableBook !== EnableBookEnum.unableBook // v2.21 去除掉不可预订房间（停、满）
  );
};

// 预订换排房-房间是否可以入住
export const getRoomReserveExchangeStatus: (room: RoomItem, isOriginRoom: boolean) => boolean = (
  room,
  isOriginRoom,
) => {
  // “停” 满” “原” 不可选择
  return (
    room?.bookingInfo?.enableBook !== EnableBookEnum.unableBook && // v2.21 去除掉不可预订房间（停、满）
    !isOriginRoom // 去除（原）
  );
};

// 计算券金额
export const batchCalcCouponValue = (
  baseInfo: { wyuid: string; occType: OccType },
  couponList: { businessDate: string; roomPrice: string; lockCouponNo: string }[],
) => {
  const couponValueMap = {};
  return Promise.all(
    couponList.map((itemInfo) => {
      const { businessDate, roomPrice, lockCouponNo } = itemInfo;
      return fetchCouponCalculate({
        ...baseInfo,
        businessDate,
        roomPrice: parseYuan2Fen(roomPrice),
        lockCouponNo,
      })
        .then((res) => res.data.couponAmount)
        .catch(() => null)
        .then((couponFaceValue) => {
          couponValueMap[itemInfo.lockCouponNo] = couponFaceValue;
        });
    }),
  ).then(() => couponValueMap);
};

// 排房列表数据数据
// v2.21 数据返回值增加 房间不可订状态 unableBookStatus--1 停  unableBookStatus--2 满(前端自定义字段)
export const formatAssignRoomList = (data) => {
  if (!data || data.length === 0) return [];
  const list = data.map((item) => {
    return {
      ...item,
      roomList: item?.roomList.map((r) => {
        let unableBookStatus = null;
        if (
          r?.bookingInfo?.enableBook === EnableBookEnum.unableBook &&
          r?.bookingInfo?.unableBookReasonType === ERoomArrangeType.RoomFix
        ) {
          unableBookStatus = CustomUnableBookStatus.Stop; // 停
        }
        if (
          r?.bookingInfo?.enableBook === EnableBookEnum.unableBook &&
          (r?.bookingInfo?.unableBookReasonType === ERoomArrangeType.BeforeBook ||
            r?.bookingInfo?.unableBookReasonType === ERoomArrangeType.AfterBook)
        ) {
          unableBookStatus = CustomUnableBookStatus.Full; // 满
        }
        return {
          ...r,
          unableBookStatus,
        };
      }),
    };
  });
  return list;
};
